/** Tiny IndexedDB store for original photo blobs, so autosave survives reloads. */
const DB_NAME = 'nextcollage';
/** Pre-rename database — its contents are moved over on first run. */
const LEGACY_DB_NAME = 'collagen';
const DB_VERSION = 1;
const STORE = 'assets';

let dbPromise: Promise<IDBDatabase> | null = null;

function openNamed(name: string, upgrade: boolean): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = upgrade ? indexedDB.open(name, DB_VERSION) : indexedDB.open(name);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function readAll(db: IDBDatabase): Promise<[IDBValidKey, Blob][]> {
  return new Promise((resolve, reject) => {
    if (!db.objectStoreNames.contains(STORE)) return resolve([]);
    const store = db.transaction(STORE, 'readonly').objectStore(STORE);
    const keysReq = store.getAllKeys();
    const valsReq = store.getAll();
    let keys: IDBValidKey[] | null = null;
    let vals: Blob[] | null = null;
    const done = () => {
      if (keys && vals) resolve(keys.map((k, i) => [k, vals![i]] as [IDBValidKey, Blob]));
    };
    keysReq.onsuccess = () => ((keys = keysReq.result), done());
    valsReq.onsuccess = () => ((vals = valsReq.result as Blob[]), done());
    keysReq.onerror = valsReq.onerror = () => reject(keysReq.error ?? valsReq.error);
  });
}

/** Carry photos over from the pre-rename database, once. */
async function migrateLegacy(db: IDBDatabase) {
  try {
    if (!indexedDB.databases) return;
    const names = (await indexedDB.databases()).map((d) => d.name);
    if (!names.includes(LEGACY_DB_NAME)) return;

    const legacy = await openNamed(LEGACY_DB_NAME, false);
    const entries = await readAll(legacy);
    legacy.close();

    if (entries.length) {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        for (const [key, blob] of entries) store.put(blob, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    }
    indexedDB.deleteDatabase(LEGACY_DB_NAME);
  } catch {
    /* migration is best-effort — a failure just means older photos are gone */
  }
}

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = openNamed(DB_NAME, true).then(async (db) => {
    await migrateLegacy(db);
    return db;
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const blobStore = {
  put: (id: string, blob: Blob) => tx('readwrite', (s) => s.put(blob, id) as IDBRequest<IDBValidKey>),
  get: (id: string) => tx<Blob | undefined>('readonly', (s) => s.get(id)),
  delete: (id: string) => tx('readwrite', (s) => s.delete(id) as unknown as IDBRequest<undefined>),
  keys: () => tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys()),
  clear: () => tx('readwrite', (s) => s.clear() as unknown as IDBRequest<undefined>),
};

/**
 * Delete blobs no document references any more.
 *
 * `keep` must be the union across every tool's document — the store is shared,
 * so pruning against one document alone would delete the other tools' photos.
 */
export async function pruneBlobs(keep: string[]) {
  try {
    const keys = await blobStore.keys();
    // An empty keep-set with blobs on disk is nearly always a bug — a failed
    // hydration racing the first autosave. Stale blobs cost far less than
    // deleting someone's photos, so bail out rather than sweep the store.
    if (!keep.length && keys.length) return;
    const keepSet = new Set(keep);
    await Promise.all(
      keys.filter((k) => typeof k === 'string' && !keepSet.has(k)).map((k) => blobStore.delete(k as string)),
    );
  } catch {
    /* storage unavailable — non-fatal */
  }
}
